import { useEffect, useState } from 'react';
import { Callout, Spinner } from '@humanforce/mango-ui-react';
import { DetailPair, SectionShell } from '../components/SectionShell';
import { listEmergencyContacts } from '../workersApi';
import type { PersonEmergencyContactResponse } from '../workersApiTypes';

// Emergency contacts are HR-mastered and READ-ONLY in payroll (BR-EC-002 / D-1). The backend
// returns HTTP 405 on direct writes, so this section renders no create/edit/delete controls.
const formatSync = (value?: string | null) => (value ? value.slice(0, 10) : null);

export const EmergencyContactsSection = ({ personId }: { personId: string }) => {
  const [contacts, setContacts] = useState<PersonEmergencyContactResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    (async () => {
      const result = await listEmergencyContacts(personId);
      if (ignore) return;
      if (result.ok) setContacts(result.items);
      else setError(result.message ?? 'Failed to load emergency contacts.');
      setLoading(false);
    })();
    return () => { ignore = true; };
  }, [personId]);

  return (
    <SectionShell title="Emergency contacts" description="Managed in HR. These records are read-only here.">
      <Callout tone="info" title="Managed in HR">
        Emergency contacts are synced from the HR system and cannot be edited in payroll.
      </Callout>

      <div style={{ marginTop: 'var(--mgo-space-40)' }}>
        {loading ? (
          <div className="mgo-cluster mgo-gap-s mgo-align-center"><Spinner /> <span className="mgo-text-s mgo-color-muted">Loading emergency contacts…</span></div>
        ) : error ? (
          <Callout tone="critical" title="Couldn't load emergency contacts">{error}</Callout>
        ) : contacts.length === 0 ? (
          <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>No emergency contacts on record.</p>
        ) : (
          <div className="mgo-stack mgo-gap-l">
            {contacts.map((contact) => {
              const synced = formatSync(contact.lastSyncedAt);
              return (
                <div key={contact.emergencyContactId} className="mgo-stack mgo-gap-s mgo-border-neutral mgo-radius-normal" style={{ padding: 'var(--mgo-space-40)' }}>
                  <div className="mgo-cluster mgo-gap-s mgo-align-center">
                    <span className="mgo-text-s mgo-weight-strong">{contact.contactName}</span>
                    <span className="mgo-text-xs mgo-color-muted">Priority {contact.priorityOrder ?? '—'}</span>
                  </div>
                  <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
                    <DetailPair label="Relationship" value={contact.relationship} />
                    <DetailPair label="Primary phone" value={contact.primaryPhone} />
                    <DetailPair label="Secondary phone" value={contact.secondaryPhone} />
                    <DetailPair label="Email" value={contact.email} />
                    <DetailPair label="Address" value={contact.address} />
                    {synced && <DetailPair label="Last synced" value={synced} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SectionShell>
  );
};
